/**
 * useProjectDetailHandler - ProjectDetail 消息处理 Hook
 * 提取自 ChatPage.tsx，处理来自 ProjectDetail 组件的消息事件
 */

import { useEffect, useRef } from 'react';
import { Conversation, Message } from '@/types';
import { generateId } from '@/lib/utils';
import { useUIStore } from '@/stores';
import { createPendingId } from '@/utils/messageUtils';

export interface UseProjectDetailHandlerOptions {
  conversationsRef: React.MutableRefObject<Conversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  projectId: string | null;
  sendMessage: (
    conversationId: string,
    content: string,
    pendingId: string,
    projectId: string,
    clientId: string | null,
    options?: any
  ) => Promise<void>;
  updateConversationMessage: (conversationId: string, message: Message) => void;
  updateConversationLoadingState: (
    conversationId: string,
    isLoading: boolean
  ) => void;
}

export function useProjectDetailHandler({
  conversationsRef,
  setConversations,
  projectId,
  sendMessage,
  updateConversationMessage,
  updateConversationLoadingState,
}: UseProjectDetailHandlerOptions): void {
  // Use refs to track current values without causing re-registration
  const projectIdRef = useRef(projectId);
  const sendMessageRef = useRef(sendMessage);
  const updateConversationMessageRef = useRef(updateConversationMessage);
  const updateConversationLoadingStateRef = useRef(
    updateConversationLoadingState
  );

  // Update refs when values change
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);
  useEffect(() => {
    updateConversationMessageRef.current = updateConversationMessage;
  }, [updateConversationMessage]);
  useEffect(() => {
    updateConversationLoadingStateRef.current = updateConversationLoadingState;
  }, [updateConversationLoadingState]);

  // Listen for messages from ProjectDetail
  useEffect(() => {
    const handleProjectDetailMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      // 🔥 架构简化：移除 pendingId 从 messageDetails，改为从已创建的对话中获取
      const {
        conversationId,
        projectId: eventProjectId,
        content,
        options,
      } = customEvent.detail;

      // 验证：只处理属于当前 projectId 的消息
      if (
        eventProjectId !== projectIdRef.current &&
        !conversationId.startsWith('temp-')
      ) {
        return;
      }
      if (!conversationId) {
        return;
      }

      // 如果 options 中包含 clientId，立即更新 conversationAgentMap
      if (options?.clientId) {
        const { agents, setConversationAgent } = useUIStore.getState();
        const agent = agents.find(a => a.id === options.clientId);
        if (agent) {
          setConversationAgent(conversationId, agent);
        }
      }

      // Check if conversation exists
      const conversation = conversationsRef.current.find(
        conv => conv.id === conversationId
      );

      // 对于 temp- 会话，不创建 placeholder
      if (!conversation && !conversationId.startsWith('temp-')) {
        console.warn(
          '⚠️ ChatPage: Conversation not found in current state, will create placeholder'
        );
        setConversations(prev => {
          if (prev.find(conv => conv.id === conversationId)) {
            return prev;
          }
          const placeholderConversation = {
            id: conversationId,
            conversationId,
            title: content.substring(0, 50),
            messages: [],
            treeData: [],
            versionPath: {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            projectId: eventProjectId,
          };
          return [placeholderConversation, ...prev];
        });
      } else if (!conversation && conversationId.startsWith('temp-')) {
        // 🔥 架构简化：不再从 messageDetails 获取 pendingId
        // sendMessage 内部会自动生成，或者从 options 中获取（如果调用方已创建占位消息）
        const pendingId = options?.pendingId || createPendingId();

        sendMessageRef
          .current(
            content,
            conversationId,
            pendingId,
            eventProjectId || '',
            options?.clientId || null,
            options
          )
          .catch(error => {
            console.error(
              '❌ ChatPage: Failed to send message from ProjectDetail:',
              error
            );
            if (updateConversationLoadingStateRef.current) {
              updateConversationLoadingStateRef.current(conversationId, false);
            }
          });

        return;
      }

      // Check if user message already exists
      const existingConv = conversationsRef.current.find(
        c => c.id === conversationId
      );
      const userMessageExists = existingConv
        ? (existingConv.messages || []).some(
            msg =>
              msg.role === 'user' &&
              msg.choices?.[0]?.blocks?.[0]?.content === content
          )
        : false;

      // Only add user message if it doesn't exist
      if (!userMessageExists) {
        const userMessage: Message = {
          id: generateId(),
          choices: [
            {
              version: 0,
              blocks: [{ type: 'text' as const, content: String(content) }],
            },
          ],
          role: 'user',
          fileIds: options?.fileIds,
          timestamp: new Date(),
        };
        updateConversationMessageRef.current(conversationId, userMessage);
      }

      // Set loading state
      if (updateConversationLoadingStateRef.current) {
        updateConversationLoadingStateRef.current(conversationId, true);
      }

      // Send the message
      // 🔥 架构简化：从 options 中获取 pendingId，或自动生成
      const pendingId = options?.pendingId || createPendingId();
      sendMessageRef
        .current(
          content,
          conversationId,
          pendingId,
          eventProjectId || '',
          options?.clientId || null,
          options
        )
        .catch(error => {
          console.error(
            '❌ ChatPage: Failed to send message from ProjectDetail:',
            error
          );
          if (updateConversationLoadingStateRef.current) {
            updateConversationLoadingStateRef.current(conversationId, false);
          }
        });
    };

    window.addEventListener('projectDetailMessage', handleProjectDetailMessage);

    return () => {
      window.removeEventListener(
        'projectDetailMessage',
        handleProjectDetailMessage
      );
    };
  }, [conversationsRef, setConversations]);
}

export default useProjectDetailHandler;
